// api/WikiSearchAPI.ts
import { Int } from "@adornis/baseql/baseqlTypes.js";
import { registerQuery, registerSubscription } from "@adornis/baseql/metadata/register.js";
import { publishFilter } from "@adornis/baseql/server/context.js";
import { loadMongoData } from "@adornis/baseql/server/data-loader.js";
import { Observable, of } from "rxjs";
import { WikiEntry } from "../db/WikiEntry.js";
import { WikiChunkedSearchResult, WikiSearchResult } from "../db/WikiSearchResult.js";
import { extractDataFromID, searchAll } from "../server/redis.js";
var wikiSearch = registerQuery({
  type: () => [WikiSearchResult],
  operationName: "wikiSearch",
  resolve: (query) => {
    return async (gqlFields) => {
      const res = await searchAll(query.toLowerCase(), 100);
      return await Promise.all(
        res.map((r) => {
          return new WikiSearchResult({
            entryID: extractDataFromID(r.id, "entry-id"),
            meta: JSON.stringify(r.value["$.meta"]),
            text: r.value["$.text"],
            score: r.value.score
          });
        })
      );
    };
  },
  params: [{ type: () => String, name: "query" }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: "bypass" } } }
});
var wikiSearchSubscription = registerSubscription({
  type: () => [WikiSearchResult],
  operationName: "wikiSearchSubscription",
  resolve: (skip, limit, query) => {
    return (gqlFields) => {
      if (skip > 0) {
        return of([]);
      }
      return new Observable((subscriber) => {
        if (query.length == 0) {
          subscriber.next([]);
          subscriber.complete();
          return;
        }
        searchAll(query.toLowerCase(), 100).then(async (res) => {
          const searchResults = res.map((r) => {
            return new WikiSearchResult({
              entryID: extractDataFromID(r.id, "entry-id"),
              meta: JSON.stringify(r.value["$.meta"]),
              text: r.value["$.text"],
              score: r.value.score
            });
          });
          const pf = await publishFilter(WikiEntry._class);
          if (pf) {
            const allIDs = [...new Set(searchResults.map((r) => r.entryID))];
            const fetched = await loadMongoData(WikiEntry, allIDs);
            subscriber.next(searchResults.filter((r) => fetched.find((f) => f._id == r.entryID)));
          } else {
            subscriber.next(searchResults);
          }
          subscriber.complete();
        });
      });
    };
  },
  params: [
    { type: () => Int, name: "skip" },
    { type: () => Int, name: "limit" },
    { type: () => String, name: "query" }
  ],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: "bypass" } } }
});
var wikiSearchChunkedSubscription = registerSubscription({
  type: () => [WikiChunkedSearchResult],
  operationName: "wikiSearchChunkedSubscription",
  resolve: (skip, limit, query) => {
    return (gqlFields) => {
      if (skip > 0) {
        return of([]);
      }
      return new Observable((subscriber) => {
        if (query.length == 0) {
          subscriber.next([]);
          subscriber.complete();
          return;
        }
        searchAll(query.toLowerCase(), 100).then(async (res) => {
          let searchResults = res.map((r) => {
            return new WikiSearchResult({
              entryID: extractDataFromID(r.id, "entry-id"),
              meta: JSON.stringify(r.value["$.meta"]),
              text: r.value["$.text"],
              score: r.value.score
            });
          });
          const pf = await publishFilter(WikiEntry._class);
          if (pf) {
            const allIDs = [...new Set(searchResults.map((r) => r.entryID))];
            const fetched = await loadMongoData(WikiEntry, allIDs);
            searchResults = searchResults.filter((r) => fetched.find((f) => f._id == r.entryID));
          }
          const chunks = WikiSearchResult.ChunkByEntryID(searchResults);
          subscriber.next(
            Object.entries(chunks).map(([entryID, results]) => {
              return new WikiChunkedSearchResult({
                entryID,
                results
              });
            })
          );
          subscriber.complete();
        });
      });
    };
  },
  params: [
    { type: () => Int, name: "skip" },
    { type: () => Int, name: "limit" },
    { type: () => String, name: "query" }
  ],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: "bypass" } } }
});
var WikiSearchAPI;
((WikiSearchAPI2) => {
  WikiSearchAPI2.search = wikiSearch;
  WikiSearchAPI2.searchChunkedSubscription = wikiSearchChunkedSubscription;
  WikiSearchAPI2.searchSubscription = wikiSearchSubscription;
})(WikiSearchAPI || (WikiSearchAPI = {}));
export {
  WikiSearchAPI,
  wikiSearch,
  wikiSearchChunkedSubscription,
  wikiSearchSubscription
};
